import React from "react";
import { Row, Col, Label, Input, Card, CardBody } from "reactstrap";
import { useTranslation } from "react-i18next";
import ReactTable2 from "components/ReactTable/ReactTable2";
import {
  getReactTableThemeFromStorage,
  getReactTableThemeDefaults,
  setReactTableThemeInStorage,
  normalizeHexColor,
  REACT_TABLE_THEME_CHANGED_EVENT,
} from "utils/reactTableTheme";

const COLOR_FIELDS = [
  "headerBg",
  "headerText",
  "rowStripeBg",
  "rowHoverBg",
  "cellText",
  "rowBorder",
  "paginationAccent",
];

const PREVIEW_COLUMNS = [
  { Header: "Name", accessor: "name", width: 140 },
  { Header: "Role", accessor: "role", width: 160 },
  { Header: "Location", accessor: "location", width: 120 },
];

const PREVIEW_DATA = [
  { name: "Airi Satou", role: "Accountant", location: "Tokyo" },
  { name: "Angelica Ramos", role: "CEO", location: "London" },
  { name: "Bradley Greer", role: "Engineer", location: "London" },
  { name: "Colleen Hurst", role: "Developer", location: "SF" },
];

/** Keeps `#` prefix while typing partial hex digits. */
function formatHexWhileTyping(raw) {
  const stripped = String(raw || "")
    .trim()
    .replace(/[^0-9A-Fa-f#]/g, "");
  if (!stripped) return "#";
  const withoutHash = stripped.replace(/^#+/, "").slice(0, 6);
  return `#${withoutHash}`;
}

const SidebarAppearanceTableColorsPanel = ({ activeColor, isLightTheme, shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const shellVariant = shellThemeVariant === "2" ? "2" : "1";
  const modeDefaults = React.useMemo(
    () => getReactTableThemeDefaults(shellVariant),
    [shellVariant]
  );
  const [draftHex, setDraftHex] = React.useState(() => {
    const stored = getReactTableThemeFromStorage();
    const mode = isLightTheme ? "light" : "dark";
    const out = {};
    for (const field of COLOR_FIELDS) {
      out[field] = stored[mode][field];
    }
    return out;
  });
  const editingMode = isLightTheme ? "light" : "dark";
  const fieldDefaults = modeDefaults[editingMode];

  React.useEffect(() => {
    const sync = () => {
      const stored = getReactTableThemeFromStorage();
      const nextDraft = {};
      for (const field of COLOR_FIELDS) {
        nextDraft[field] = stored[editingMode][field];
      }
      setDraftHex(nextDraft);
    };
    sync();
    window.addEventListener(REACT_TABLE_THEME_CHANGED_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(REACT_TABLE_THEME_CHANGED_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [editingMode]);

  const commitHex = (field, rawValue) => {
    const fallback = fieldDefaults[field];
    const normalized = normalizeHexColor(rawValue, fallback);
    setDraftHex((prev) => ({ ...prev, [field]: normalized }));
    setReactTableThemeInStorage(editingMode, { [field]: normalized }, shellVariant);
  };

  const handleTextChange = (field, rawValue) => {
    setDraftHex((prev) => ({ ...prev, [field]: formatHexWhileTyping(rawValue) }));
  };

  const handleTextBlur = (field) => {
    commitHex(field, draftHex[field]);
  };

  const handleColorPickerChange = (field, pickerValue) => {
    commitHex(field, pickerValue);
  };

  return (
    <div className="sidebar-appearance-table-colors-panel sidebar-appearance-theme-controls sidebar-appearance-theme-mode">
      <Row className="mb-3">
        {COLOR_FIELDS.map((field) => {
          const displayValue = draftHex[field] || fieldDefaults[field];
          const pickerValue = normalizeHexColor(displayValue, fieldDefaults[field]);
          return (
            <Col xs="12" md="6" lg="4" key={field} className="mb-3">
              <Label className="sidebar-appearance-section-label mb-1 d-block">
                {t(`sidebarAppearance.tableColorsSection.${field}`)}
              </Label>
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                <input
                  type="color"
                  value={pickerValue}
                  onChange={(e) => handleColorPickerChange(field, e.target.value)}
                  aria-label={t(`sidebarAppearance.tableColorsSection.${field}`)}
                  style={{
                    width: 36,
                    height: 36,
                    padding: 0,
                    border: "1px solid rgba(128, 128, 128, 0.4)",
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                />
                <Input
                  type="text"
                  value={displayValue}
                  onChange={(e) => handleTextChange(field, e.target.value)}
                  onBlur={() => handleTextBlur(field)}
                  bsSize="sm"
                  placeholder="#000000"
                  maxLength={7}
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                />
              </div>
            </Col>
          );
        })}
      </Row>
      <Card className="mb-0 sidebar-appearance-table-preview-card">
        <CardBody className="p-2 pt-3">
          <p className="sidebar-appearance-section-label mb-2">
            {t("sidebarAppearance.tableColorsSection.preview")}
          </p>
          <div
            className="sidebar-appearance-table-preview-wrap"
            style={{ overflowX: "auto", maxWidth: "100%" }}
          >
            <ReactTable2
              activeColor={activeColor}
              columns={PREVIEW_COLUMNS}
              data={PREVIEW_DATA}
              className="-striped -highlight sticky-stock-table"
              disableViewportClamp
            />
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default SidebarAppearanceTableColorsPanel;
